import type { BlogArticle } from '@/lib/blog-data'

export const post: BlogArticle = {
    slug: 'tesla-sentry-mode-custom-sounds',
    title: 'Can You Change Tesla Sentry Mode Sound? (Here\'s How)',
    excerpt: 'Tesla Sentry Mode uses your lock sound to warn people near your car. Here\'s how to swap it: USB setup, file format, and all-model instructions.',
  image: '/images/blog/generated/tesla-sentry-mode-custom-sounds.png',
    content: `
# Tesla Sentry Mode & Custom Lock Sounds: Full Guide

Sentry Mode is one of Tesla's security features, and it's closely connected to custom lock sounds. This guide explains everything about how these features work together.

## What is Sentry Mode?

Sentry Mode is Tesla's vehicle security system that:
- Monitors surroundings when parked
- Records incidents with onboard cameras
- Alerts you to potential threats
- Uses exterior speakers for audio warnings

## How Custom Sounds Work with Sentry Mode

### Lock Sounds
Custom lock sounds are set in Toybox → Boombox → Lock Sound, not in Sentry Mode:
- Your custom sound plays when locking the car
- Tesla does not document a separate custom unlock sound
- It replaces the default Tesla chime

### Sentry Mode Alerts
Sentry Mode has different alert levels:
- **Standby**: Cameras recording, no audio
- **Alert**: Something detected, headlights flash
- **Alarm**: Threat detected, alarm sounds

Custom lock sounds are separate from the alarm sound, which remains Tesla's default.

## Setting Up Custom Sounds via Sentry Mode

### Step 1: Prepare Your Sound
- Download a sound from our [library](/sounds)
- Ensure it's named \`LockChime.wav\`
- Place in \`/Boombox/\` folder on USB

### Step 2: Enable Sentry Mode
1. Go to **Controls > Safety & Security**
2. Enable **Sentry Mode**
3. Look for **Custom Sound** options

### Step 3: Select Your Sound
1. Insert USB with your sound file
2. Navigate to sound settings
3. Select your custom chime
4. Test by locking your Tesla

## Sentry Mode Sound Settings

### Available Options
Depending on your Tesla model and software version:
- Lock sound customization
- Unlock sound customization
- Boombox integration
- Volume controls

### What You Can't Customize
Currently, Tesla doesn't allow customizing:
- The Sentry Mode alarm itself
- Camera startup sounds
- App notification sounds

## Best Sounds for Sentry Mode

Consider sounds that:
- Are noticeable but not alarming
- Indicate security status
- Don't disturb neighbors at night

Good fits:
- [Sci-fi sounds](/sounds/category/sound-effects): security beeps, futuristic chimes, HAL 9000
- [Meme sounds](/sounds/category/memes): short, punchy, instantly recognizable

## Sentry Mode + Boombox

Sentry Mode enables access to Boombox features:
- Custom lock sounds
- Summon sounds
- Fun audio while parked

Both features share the same USB and folder structure.

## Troubleshooting

### Sound Not Playing with Sentry Mode
- Verify Sentry Mode is enabled
- Check USB is properly formatted
- Ensure file is in correct location
- Re-select sound in settings

### Sound Plays Inconsistently
- Check battery level (Sentry Mode may disable on low battery)
- Verify software is up to date
- Try a different USB drive

## FAQ

### Does the custom sound play at night?
Yes, unless you've enabled "Exclude Home" or time-based settings for Sentry Mode.

### Will custom sounds drain my battery?
The sound itself uses minimal power. Sentry Mode's camera monitoring is what uses battery.

### Can I have different sounds for lock and alarm?
Currently, no. The alarm sound is not customizable.

## See Also

- [Installation Guide](/guide): Full setup walkthrough
- [Troubleshooting Guide](/blog/troubleshooting-tesla-custom-sounds): Fix common issues
- [LockChime.wav format & USB path](/lockchime): File specs for all models
- [Lock sound keeps resetting after update?](/blog/tesla-lock-sound-resets-after-update): Permanent fix
- [Tesla unlock sound: what you can customize](/blog/tesla-unlock-sound-how-to-set-up): Honest answer on lock vs unlock
- [Model 3 sounds](/sounds/model/model-3) · [Model Y sounds](/sounds/model/model-y) · [Model S sounds](/sounds/model/model-s) · [Model X sounds](/sounds/model/model-x) · [Cybertruck sounds](/sounds/model/cybertruck)
    `,
    author: 'Tesla Lock Sound Team',
    publishedAt: '2026-01-08',
    category: 'tutorials',
    tags: ['sentry-mode', 'security', 'setup', 'guide'],
    readingTime: 5,
    soundCategory: 'Alarms & Alerts',
    soundLimit: 4,
    featured: false
  }
